/**
 * SSR-safety helpers and documentation.
 *
 * Checklist for code that runs under Vike SSR / shared packages:
 * 1. Never touch window/document/localStorage/navigator at module top-level.
 * 2. Guard browser APIs with `typeof window !== 'undefined'` or ClientOnly.
 * 3. Prefer import.meta.env.SSR for dead-code elimination.
 * 4. Side effects (GTM, analytics, cart cookie sync) belong in ClientOnly islands.
 * 5. Prefer process.env / env.node for BFF URLs on the server.
 *
 * Light audit (layout + shared libs used on SSR routes) — July 2026:
 * - cart-context: localStorage guarded ✓
 * - providers: DeviceProvider + checkout listeners client-only ✓
 * - env facade: SSR uses env.node ✓
 * - Emotion: prerendered HTML includes `<style data-emotion>` (vike-react stream);
 *   CacheProvider + createEmotionCache per request in pages/+Wrapper ✓
 * - afrodita-components / storefront-funnel: still audit new modules on change
 */

/** True when this module graph is evaluating under Vite SSR. */
export function isSsrRuntime(): boolean {
  return import.meta.env.SSR === true;
}

/** Safe window accessor — null on server. */
export function getBrowserWindow(): Window | null {
  return typeof window !== 'undefined' ? window : null;
}

/** Safe document accessor — null on server. */
export function getBrowserDocument(): Document | null {
  return typeof document !== 'undefined' ? document : null;
}

/** True when running on the server (no window global). */
export function isServer(): boolean {
  return typeof window === 'undefined';
}

/**
 * ESLint: `no-restricted-globals` for window/document/localStorage/navigator
 * covers `pages/**` and `src/lib/ssr/**` (see .eslintrc.json). Allowlisted:
 * pages/(csr)/*, *client*, ClientOnly, safety.ts, tests.
 */
export const SSR_SAFETY_NOTES = [
  'No window/document/localStorage/navigator at module scope',
  'Use ClientOnly for personalization / analytics',
  'Use env.node / STOREFRONT_BFF_INTERNAL_URL on SSR',
  'CSR fallback on render errors (Vike +error / spa.html)',
] as const;
